import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  MenuItem,
  Grid,
  Tooltip,
  FormControlLabel,
  Switch
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Email as EmailIcon,
  Telegram as TelegramIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';

const AdvertisementList = () => {
  const [advertisements, setAdvertisements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [advertisementToDelete, setAdvertisementToDelete] = useState(null);
  const [sendEmailDialogOpen, setSendEmailDialogOpen] = useState(false);
  const [sendTelegramDialogOpen, setSendTelegramDialogOpen] = useState(false);
  const [advertisementToSend, setAdvertisementToSend] = useState(null);
  const [sendingInProgress, setSendingInProgress] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [testEmailMode, setTestEmailMode] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testTelegramMode, setTestTelegramMode] = useState(false);
  const [testChatId, setTestChatId] = useState('');
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchAdvertisements();
  }, [page, rowsPerPage, statusFilter, searchQuery]);

  const fetchAdvertisements = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: searchQuery || undefined
      };
      
      const response = await axios.get('/api/advertisements', { params });
      
      setAdvertisements(response.data.advertisements);
      setTotalCount(response.data.totalItems || 0);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching advertisements:', err);
      setError('Ошибка при загрузке рекламных материалов');
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleSearchQueryChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleDeleteClick = (advertisement) => {
    setAdvertisementToDelete(advertisement);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!advertisementToDelete) return;
    
    try {
      await axios.delete(`/api/advertisements/${advertisementToDelete.id}`);
      setDeleteDialogOpen(false);
      setAdvertisementToDelete(null);
      fetchAdvertisements();
    } catch (err) {
      console.error('Error deleting advertisement:', err);
      setError('Ошибка при удалении рекламного материала');
    }
  };

  const handleSendEmailClick = (advertisement) => {
    // Проверяем статус рекламы перед отправкой
    if (advertisement.status !== 'ready' && advertisement.status !== 'telegram_sent') {
      setError('Реклама должна иметь статус "ready" или "telegram_sent" для отправки по Email. Текущий статус: ' + advertisement.status);
      return;
    }
    
    setAdvertisementToSend(advertisement);
    setSendEmailDialogOpen(true);
  };

  const handleSendTelegramClick = (advertisement) => {
    // Проверяем статус рекламы перед отправкой
    if (advertisement.status !== 'ready' && advertisement.status !== 'email_sent') {
      setError('Реклама должна иметь статус "ready" или "email_sent" для отправки в Telegram. Текущий статус: ' + advertisement.status);
      return;
    }
    
    setAdvertisementToSend(advertisement);
    setSendTelegramDialogOpen(true);
  };

  const handleSendEmailConfirm = async () => {
    if (!advertisementToSend) return;
    
    try {
      setSendingInProgress(true);
      setSendResult(null);
      
      // Если включен тестовый режим, отправляем на тестовый email
      const endpoint = `/api/advertisements/${advertisementToSend.id}/send/email`;
      // Даже если testEmail пустой, мы всё равно передаём его, чтобы бэкенд использовал администраторский email
      const payload = testEmailMode ? { testEmail: testEmail.trim() } : {};
      
      const response = await axios.post(endpoint, payload);
      
      setSendResult({
        success: response.data.successCount,
        error: response.data.errorCount,
        total: response.data.successCount + response.data.errorCount,
        message: response.data.message,
        testMode: response.data.testMode,
        sentRecords: response.data.sentRecords
      });
      
      // Обновляем список только если это не тестовая отправка
      if (!testEmailMode) {
        fetchAdvertisements();
      }
    } catch (err) {
      console.error('Error sending email advertisement:', err);
      setSendResult({
        error: err.response?.data?.error || err.message || 'Ошибка при отправке рекламы по email'
      });
    } finally {
      setSendingInProgress(false);
    }
  };

  const handleSendTelegramConfirm = async () => {
    if (!advertisementToSend) return;
    
    try {
      setSendingInProgress(true);
      setSendResult(null);
      
      // Формируем тело запроса
      const requestBody = {};
      
      // Используем значение из состояния testChatId, если включен тестовый режим
      if (testTelegramMode) {
        // Даже если testChatId пустой, мы всё равно передаём его, чтобы бэкенд использовал администраторский chatId
        requestBody.testChatId = testChatId.trim();
      }
      
      console.log('Отправка запроса с testChatId:', testTelegramMode ? testChatId : 'не указан (массовая рассылка)');
      console.log('Тело запроса:', requestBody);
      
      // Устанавливаем заголовок Content-Type для правильной обработки JSON
      const response = await axios.post(
        `/api/advertisements/${advertisementToSend.id}/send/telegram`, 
        requestBody,
        { headers: { 'Content-Type': 'application/json' } }
      );
      
      setSendResult(response.data);
      setSendingInProgress(false);
    } catch (err) {
      console.error('Error sending telegram advertisement:', err);
      setSendResult({ error: `Ошибка при отправке рекламы в Telegram: ${err.response?.data?.error || err.message}` });
      setSendingInProgress(false);
    }
  };

  const handleCloseSendDialog = () => {
    setSendEmailDialogOpen(false);
    setSendTelegramDialogOpen(false);
    setAdvertisementToSend(null);
    setSendResult(null);
    setTestEmailMode(false);
    setTestEmail('');
    setTestTelegramMode(false);
    setTestChatId('');
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'draft':
        return <Chip label="Черновик" color="default" size="small" />;
      case 'ready':
        return <Chip label="Готов к отправке" color="primary" size="small" />;
      case 'sent':
        return <Chip label="Отправлен" color="success" size="small" />;
      case 'telegram_sent':
        return <Chip label="Отправлен в Telegram" color="info" size="small" />;
      case 'email_sent':
        return <Chip label="Отправлен по Email" color="info" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Нет данных';
    
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true, locale: ru });
    } catch (error) {
      return dateString;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Управление рекламными материалами
        </Typography>
        <Button
          component={Link}
          to="/admin/advertisements/create"
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
        >
          Создать рекламу
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              label="Поиск"
              variant="outlined"
              fullWidth
              size="small"
              value={searchQuery}
              onChange={handleSearchQueryChange}
              placeholder="Поиск по названию или описанию"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label="Статус"
              variant="outlined"
              fullWidth
              size="small"
              value={statusFilter}
              onChange={handleStatusFilterChange}
            >
              <MenuItem value="all">Все статусы</MenuItem>
              <MenuItem value="draft">Черновики</MenuItem>
              <MenuItem value="published">Опубликованные</MenuItem>
              <MenuItem value="archived">Архивные</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Box>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
          <Table stickyHeader aria-label="advertisements table">
            <TableHead>
              <TableRow>
                <TableCell>Название</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Автор</TableCell>
                <TableCell>Создано</TableCell>
                <TableCell>Обновлено</TableCell>
                <TableCell align="center">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Загрузка...
                  </TableCell>
                </TableRow>
              ) : advertisements.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Рекламные материалы не найдены
                  </TableCell>
                </TableRow>
              ) : (
                advertisements.map((advertisement) => (
                  <TableRow key={advertisement.id} hover>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                        {advertisement.title}
                      </Typography>
                    </TableCell>
                    <TableCell>{getStatusChip(advertisement.status)}</TableCell>
                    <TableCell>
                      {advertisement.author ? advertisement.author.name : 'Нет данных'}
                    </TableCell>
                    <TableCell>{formatDate(advertisement.createdAt)}</TableCell>
                    <TableCell>{formatDate(advertisement.updatedAt)}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Tooltip title="Редактировать">
                          <IconButton 
                            component={Link}
                            to={`/admin/advertisements/edit/${advertisement.id}`}
                            size="small"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Удалить">
                          <IconButton 
                            size="small"
                            onClick={() => handleDeleteClick(advertisement)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Отправить по Email">
                          <span>
                            <IconButton 
                              color="primary" 
                              onClick={() => handleSendEmailClick(advertisement)}
                              disabled={advertisement.status !== 'ready' && advertisement.status !== 'telegram_sent'}
                            >
                              <EmailIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Отправить в Telegram">
                          <span>
                            <IconButton 
                              color="primary" 
                              onClick={() => handleSendTelegramClick(advertisement)}
                              disabled={advertisement.status !== 'ready' && advertisement.status !== 'email_sent'}
                            >
                              <TelegramIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Статистика отправки">
                          <IconButton 
                            component={Link}
                            to={`/admin/advertisements/results/${advertisement.id}`}
                            size="small"
                          >
                            <AssessmentIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Строк на странице:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count !== -1 ? count : 'более чем ' + to}`}
        />
      </Paper>

      {/* Диалог подтверждения удаления */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Подтверждение удаления</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Вы действительно хотите удалить рекламный материал "{advertisementToDelete?.title}"?
            Это действие нельзя отменить.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог отправки по Email */}
      <Dialog
        open={sendEmailDialogOpen}
        onClose={handleCloseSendDialog}
        fullWidth
      >
        <DialogTitle>
          {sendResult ? 'Результат отправки' : 'Отправить рекламу по Email'}
        </DialogTitle>
        <DialogContent>
          {sendingInProgress ? (
            <DialogContentText>
              Отправка рекламы... Пожалуйста, подождите.
            </DialogContentText>
          ) : sendResult ? (
            <Box>
              {sendResult.error ? (
                <Typography color="error">{sendResult.error}</Typography>
              ) : (
                <>
                  <Typography variant="body1" gutterBottom>
                    {sendResult.message}
                  </Typography>
                  <Typography variant="body2">
                    Успешно отправлено: {sendResult.success} из {sendResult.total}
                  </Typography>
                  {typeof sendResult.error === "number" && sendResult.error > 0 && (
                    <Typography variant="body2" color="error">
                      Ошибок: {sendResult.error}
                    </Typography>
                  )}
                  {sendResult.testMode && sendResult.sentRecords && (
                    <Box mt={2}>
                      <Typography variant="subtitle2">Результаты тестовой отправки:</Typography>
                      {sendResult.sentRecords.map((record, index) => (
                        <Typography key={index} variant="body2">
                          {record.email}: {record.status === 'sent' ? 'Успешно' : 'Ошибка'}
                          {record.isAdminTest && ' (Тестовое письмо для администратора)'}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </>
              )}
            </Box>
          ) : (
            <>
              <DialogContentText>
                {testEmailMode 
                  ? 'Вы собираетесь отправить тестовое письмо с рекламным материалом.'
                  : `Вы собираетесь отправить рекламный материал "${advertisementToSend?.title}" всем активным подписчикам.`
                }
              </DialogContentText>
              
              <Box mt={2} mb={2}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={testEmailMode} 
                      onChange={(e) => setTestEmailMode(e.target.checked)} 
                      color="primary"
                    />
                  }
                  label="Тестовая отправка"
                />
                
                {testEmailMode && (
                  <TextField
                    margin="dense"
                    label="Email для тестовой отправки"
                    type="email"
                    fullWidth
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    helperText="Оставьте пустым для использования Email администратора из .env"
                  />
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!sendingInProgress && (
            <>
              <Button onClick={handleCloseSendDialog}>
                {sendResult ? 'Закрыть' : 'Отмена'}
              </Button>
              {!sendResult && (
                <Button 
                  onClick={handleSendEmailConfirm} 
                  color="primary" 
                  autoFocus
                  disabled={false}
                >
                  {testEmailMode ? 'Отправить тестовое письмо' : 'Отправить всем'}
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Диалог отправки в Telegram */}
      <Dialog
        open={sendTelegramDialogOpen}
        onClose={handleCloseSendDialog}
        fullWidth
      >
        <DialogTitle>
          {sendResult ? 'Результат отправки' : 'Отправить рекламу в Telegram'}
        </DialogTitle>
        <DialogContent>
          {sendingInProgress ? (
            <DialogContentText>
              Отправка рекламы... Пожалуйста, подождите.
            </DialogContentText>
          ) : sendResult ? (
            <Box>
              {sendResult.error ? (
                <Typography color="error">{sendResult.error}</Typography>
              ) : (
                <>
                  <Typography variant="body1" gutterBottom>
                    {sendResult.message}
                  </Typography>
                  <Typography variant="body2">
                    Успешно отправлено: {sendResult.success} из {sendResult.total}
                  </Typography>
                  {typeof sendResult.error === "number" && sendResult.error > 0 && (
                    <Typography variant="body2" color="error">
                      Ошибок: {sendResult.error}
                    </Typography>
                  )}
                  {sendResult.testMode && sendResult.sentRecords && (
                    <Box mt={2}>
                      <Typography variant="subtitle2">Результаты тестовой отправки:</Typography>
                      {sendResult.sentRecords.map((record, index) => (
                        <Typography key={index} variant="body2">
                          Chat ID: {record.chatId}: {record.status === 'sent' ? 'Успешно' : 'Ошибка'}
                          {record.isAdminTest && ' (Тестовое сообщение для администратора)'}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </>
              )}
            </Box>
          ) : (
            <>
              <DialogContentText>
                {testTelegramMode 
                  ? 'Вы собираетесь отправить тестовое сообщение с рекламным материалом.'
                  : `Вы собираетесь отправить рекламный материал "${advertisementToSend?.title}" всем активным подписчикам в Telegram.`
                }
              </DialogContentText>
              
              <Box mt={2} mb={2}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={testTelegramMode} 
                      onChange={(e) => setTestTelegramMode(e.target.checked)} 
                      color="primary"
                    />
                  }
                  label="Тестовая отправка"
                />
                
                {testTelegramMode && (
                  <TextField
                    margin="dense"
                    label="Chat ID для тестовой отправки"
                    type="text"
                    fullWidth
                    value={testChatId}
                    onChange={(e) => setTestChatId(e.target.value)}
                    helperText="Оставьте пустым для использования Chat ID администратора из .env"
                  />
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!sendingInProgress && (
            <>
              <Button onClick={handleCloseSendDialog}>
                {sendResult ? 'Закрыть' : 'Отмена'}
              </Button>
              {!sendResult && (
                <Button 
                  onClick={handleSendTelegramConfirm} 
                  color="primary" 
                  autoFocus
                  disabled={false}
                >
                  {testTelegramMode ? 'Отправить тестовое сообщение' : 'Отправить всем'}
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdvertisementList;
